import { minutesFromHHmm, zonedParts } from '@/lib/menu/availability';

export const WEEKDAYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

export type Weekday = (typeof WEEKDAYS)[number];

export type BranchShift = {
  open: string;
  close: string;
};

export type BranchDayHours = {
  closed: boolean;
  open?: string;
  close?: string;
  /**
   * Multiple open/close windows for one weekday (e.g. a branch that runs a
   * morning shift 09:00–17:00 and a night shift 21:00–02:00). `open`/`close`
   * remain populated as aliases of the FIRST shift so older readers that only
   * understand a single window keep working. Overnight shifts (close <= open)
   * are supported inside the array the same way they were for a single window.
   */
  shifts?: BranchShift[];
};

export type BranchOperatingHours = Record<Weekday, BranchDayHours>;

function isMetadataRecord(input: unknown): input is Record<string, unknown> {
  return Boolean(input) && typeof input === 'object' && !Array.isArray(input);
}

function isBranchShift(input: unknown): input is BranchShift {
  return (
    Boolean(input) &&
    typeof input === 'object' &&
    !Array.isArray(input) &&
    typeof (input as BranchShift).open === 'string' &&
    typeof (input as BranchShift).close === 'string'
  );
}

export function defaultBranchOperatingHours(): BranchOperatingHours {
  return WEEKDAYS.reduce((acc, day) => {
    acc[day] = { closed: true };
    return acc;
  }, {} as BranchOperatingHours);
}

// The list of usable open/close windows for a (normalized) day. Reads either the
// multi-shift array or falls back to the legacy single open/close pair.
export function normalizeDayShifts(day: BranchDayHours | undefined): BranchShift[] {
  if (!day || day.closed) return [];
  if (Array.isArray(day.shifts)) {
    const fromShifts = day.shifts.filter(isBranchShift);
    if (fromShifts.length > 0) return fromShifts;
  }
  if (day.open && day.close) return [{ open: day.open, close: day.close }];
  return [];
}

function parseHour(value: string | undefined): number | null {
  if (!value) return null;
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  return Number(match[1]);
}

/**
 * R04.7 (doc 04 §4.6): a venue that closes after midnight (e.g. a bar at 02:00)
 * must not have one night split across two reporting days. The reporting day
 * begins at the latest *overnight* close hour — the only case the rule names.
 *
 * We only treat a shift as overnight when its close is at or before its open
 * (close <= open, e.g. 21:00–02:00). Venues that close before midnight keep the
 * default midnight boundary, so their early-morning lull is not mis-attributed to
 * the previous day. Returns the boundary hour in [0, 23]; 0 means "use midnight".
 */
export function resolveDayStartHour(hours: BranchOperatingHours): number {
  let boundary = 0;
  for (const day of WEEKDAYS) {
    for (const shift of normalizeDayShifts(hours[day])) {
      const open = parseHour(shift.open);
      const close = parseHour(shift.close);
      if (open === null || close === null) continue;
      if (close <= open) boundary = Math.max(boundary, close);
    }
  }
  return boundary;
}

export function readBranchOperatingHours(raw: unknown): BranchOperatingHours {
  const record = isMetadataRecord(raw) ? raw : {};
  return WEEKDAYS.reduce((acc, day) => {
    const dayValue = record[day];
    if (isMetadataRecord(dayValue) && dayValue.closed !== true) {
      const shifts = Array.isArray(dayValue.shifts) ? dayValue.shifts.filter(isBranchShift) : [];
      if (shifts.length === 0) {
        const open = typeof dayValue.open === 'string' ? dayValue.open : undefined;
        const close = typeof dayValue.close === 'string' ? dayValue.close : undefined;
        if (open && close) {
          shifts.push({ open, close });
        }
      }
      if (shifts.length > 0) {
        const first = shifts[0];
        acc[day] = { closed: false, open: first.open, close: first.close, shifts };
      } else {
        acc[day] = { closed: true };
      }
    } else {
      acc[day] = { closed: true };
    }
    return acc;
  }, {} as BranchOperatingHours);
}

export function buildBranchOperatingHoursRecord(hours: BranchOperatingHours): Record<string, unknown> {
  return WEEKDAYS.reduce((acc, day) => {
    const value = hours[day];
    if (value.closed) {
      acc[day] = { closed: true };
      return acc;
    }
    const shifts = normalizeDayShifts(value);
    if (shifts.length === 0) {
      acc[day] = { closed: true };
      return acc;
    }
    const first = shifts[0];
    // Both the legacy single-window pair AND the multi-shift array are written
    // so older raw readers (AI context builders, Places, etc.) that only look
    // at `open`/`close` still resolve the first shift correctly.
    acc[day] = { closed: false, open: first.open, close: first.close, shifts };
    return acc;
  }, {} as Record<string, unknown>);
}

export function isBranchOpenNow(
  hours: BranchOperatingHours,
  timezone: string,
  referenceDate: Date = new Date(),
): boolean {
  const { weekday, minutes } = zonedParts(referenceDate, timezone || 'UTC');
  const today = WEEKDAYS[weekday];
  const dayHours = hours[today];

  return normalizeDayShifts(dayHours).some((shift) => {
    const openMinutes = minutesFromHHmm(shift.open);
    const closeMinutes = minutesFromHHmm(shift.close);

    if (openMinutes < 0 || closeMinutes < 0) {
      return false;
    }

    if (closeMinutes <= openMinutes) {
      // Overnight range (e.g. open 21:00, close 02:00) -- open if at/after open
      // OR before close on the next day.
      return minutes >= openMinutes || minutes < closeMinutes;
    }

    return minutes >= openMinutes && minutes < closeMinutes;
  });
}

// The shift that covers the given instant (local timezone), or null when the
// branch is closed. Used to render "Open now · until <close>" accurately when a
// branch runs multiple shifts in a day.
export function getActiveShift(
  hours: BranchOperatingHours,
  timezone: string,
  referenceDate: Date = new Date(),
): BranchShift | null {
  const { weekday, minutes } = zonedParts(referenceDate, timezone || 'UTC');
  const dayHours = hours[WEEKDAYS[weekday]];

  for (const shift of normalizeDayShifts(dayHours)) {
    const openMinutes = minutesFromHHmm(shift.open);
    const closeMinutes = minutesFromHHmm(shift.close);

    if (openMinutes < 0 || closeMinutes < 0) continue;

    const openNow =
      closeMinutes <= openMinutes
        ? minutes >= openMinutes || minutes < closeMinutes
        : minutes >= openMinutes && minutes < closeMinutes;

    if (openNow) return shift;
  }

  return null;
}

// The offset (ms) of `timezone` at a given UTC instant. Uses Intl so it is
// correct across DST without pulling in a tz database.
function timezoneOffsetMs(date: Date, timezone: string): number {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = formatter.formatToParts(date);
  const map: Record<string, string> = {};
  for (const part of parts) {
    if (part.type !== 'literal') map[part.type] = part.value;
  }
  const asUtc = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour),
    Number(map.minute),
    Number(map.second),
  );
  return asUtc - date.getTime();
}

// Converts a local calendar date (`YYYY-MM-DD`) + minutes-since-midnight into
// the absolute UTC instant in `timezone`. The guess-then-correct approach is
// off by at most one hour only at the single hour a DST transition lands on
// the window boundary, which is acceptable for scheduling windows.
function zonedTimeToUtc(localDate: string, minutes: number, timezone: string): Date {
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const [year, month, day] = localDate.split('-').map(Number);
  const guess = new Date(Date.UTC(year, month - 1, day, hour, minute));
  return new Date(guess.getTime() - timezoneOffsetMs(guess, timezone));
}

/**
 * Midnight at the start of `atDate`'s calendar day **in the branch's timezone**.
 *
 * Reports that mean "today" have to mean today where the restaurant is, not where the
 * server happens to run. POS analytics defaulted its window to `setHours(0,0,0,0)` on
 * the server clock, so a branch in any zone but the server's cut its trading day at the
 * wrong moment , the same class of mistake as a business stored on `UTC` while its
 * branch runs on `Africa/Accra`.
 */
export function startOfBranchDay(timezone: string, atDate: Date = new Date()): Date {
  const effectiveTimezone = timezone || 'UTC';
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: effectiveTimezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  // en-CA yields YYYY-MM-DD, which is what zonedTimeToUtc expects.
  return zonedTimeToUtc(formatter.format(atDate), 0, effectiveTimezone);
}

/**
 * The earliest instant at or after `fromDate` at which the branch is open --
 * i.e. "the next opening" when it's currently closed, or `fromDate` itself
 * when it's already open. Powers scheduling an order for the next window a
 * closed branch opens (customer-facing) and validating/auto-rescheduling
 * future-dated order times (server-side). Returns null when the branch never
 * opens within the scan horizon.
 */
export function findNextOpenInstant(
  hours: BranchOperatingHours,
  timezone: string,
  fromDate: Date = new Date(),
  maxDays = 14,
): Date | null {
  const effectiveTimezone = timezone || 'UTC';
  if (isBranchOpenNow(hours, effectiveTimezone, fromDate)) return fromDate;

  let bestStart: Date | null = null;

  for (let offset = 0; offset <= maxDays; offset += 1) {
    const dayInstant = new Date(fromDate.getTime() + offset * 86_400_000);
    const parts = zonedParts(dayInstant, effectiveTimezone);
    const dayHours = hours[WEEKDAYS[parts.weekday]];

    for (const shift of normalizeDayShifts(dayHours)) {
      const openMinutes = minutesFromHHmm(shift.open);
      const closeMinutes = minutesFromHHmm(shift.close);
      if (openMinutes < 0 || closeMinutes < 0) continue;

      const windowStart = zonedTimeToUtc(parts.date, openMinutes, effectiveTimezone);
      const windowEnd =
        closeMinutes <= openMinutes
          ? zonedTimeToUtc(parts.date, closeMinutes, effectiveTimezone).getTime() + 86_400_000
          : zonedTimeToUtc(parts.date, closeMinutes, effectiveTimezone).getTime();

      if (windowEnd > fromDate.getTime() && windowStart.getTime() > fromDate.getTime()) {
        if (!bestStart || windowStart.getTime() < bestStart.getTime()) {
          bestStart = windowStart;
        }
      }
    }
  }

  return bestStart;
}
